// PortfolioPage.js — Main portfolio container with sub-tab navigation
// Shared by FamilyOfficeDashboard and IndividualInvestorDashboard

import React, { useState, useMemo } from 'react';
import {
  BarChart3, Briefcase, Calendar, TrendingUp, Shield, Globe, ClipboardCheck
} from 'lucide-react';

import OverviewTab from './OverviewTab';
import HoldingsTab from './HoldingsTab';
import CashFlowsTab from './CashFlowsTab';
import PerformanceTab from './PerformanceTab';
import RiskTab from './RiskTab';
import FxOverlayTab from './FxOverlayTab';
import GovernanceTab from './GovernanceTab';

import usePortfolioData from '../../hooks/usePortfolioData';

const SUB_TABS = [
  { key: 'overview', label: 'Overview', icon: BarChart3 },
  { key: 'holdings', label: 'Holdings', icon: Briefcase },
  { key: 'cashflows', label: 'Cash Flows', icon: Calendar },
  { key: 'performance', label: 'Performance', icon: TrendingUp },
  { key: 'risk', label: 'Risk', icon: Shield },
  { key: 'fx', label: 'Foreign Exchange Overlay', icon: Globe },
  { key: 'governance', label: 'Governance', icon: ClipboardCheck },
];

const PortfolioPage = ({ role }) => {
  const [activeSubTab, setActiveSubTab] = useState('overview');

  const {
    loading,
    positions: allPositions,
    performance,
    benchmarks,
    fxHedges,
    cashFlows,
    riskLimits,
    stressScenarios,
    governance,
  } = usePortfolioData();

  // Filter positions by role:
  // investor_family_office / investor_admin: sees all entities
  // investor_individual / investor_analyst: sees only personal account (ENT-003)
  const positions = useMemo(() => {
    if (role === 'investor_analyst' || role === 'investor_individual') {
      return allPositions.filter(p => p.entity === 'ENT-003');
    }
    return allPositions;
  }, [role, allPositions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sub-tab navigation */}
      <div className="bg-white rounded-none border border-zinc-200 p-1 flex flex-wrap gap-1">
        {SUB_TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeSubTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveSubTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs font-mono uppercase tracking-[0.12em] transition-all ${
                isActive
                  ? 'bg-black text-white'
                  : 'bg-white text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Sub-tab content */}
      {activeSubTab === 'overview' && (
        <OverviewTab positions={positions} performanceData={performance} />
      )}

      {activeSubTab === 'holdings' && (
        <HoldingsTab positions={positions} role={role} />
      )}

      {activeSubTab === 'cashflows' && (
        <CashFlowsTab positions={positions} role={role} cashFlows={cashFlows} />
      )}

      {activeSubTab === 'performance' && (
        <PerformanceTab positions={positions} performance={performance} benchmarks={benchmarks} />
      )}

      {activeSubTab === 'risk' && (
        <RiskTab positions={positions} riskLimits={riskLimits} stressScenarios={stressScenarios} />
      )}

      {activeSubTab === 'fx' && (
        <FxOverlayTab positions={positions} role={role} fxHedges={fxHedges} />
      )}

      {activeSubTab === 'governance' && (
        <GovernanceTab role={role} governance={governance} />
      )}
    </div>
  );
};

export default PortfolioPage;
